import { useSitesInfo } from '@newfold/huapi-js';

import { queryClient } from '~/components/MFEProvider/lib/HttpClient/queryClient';
import { useTenant } from '~/components/TenantProvider';
import {
  EventEntryQueryData,
  eventWatchRequestPropOptions,
  generateEventEntryQueryKey,
  ResourceIdTypes,
} from '~/hooks/useAccountEvents';
import { getStagingPromotionQueryKeys } from '~/scenes/Site/scenes/Cloud/scenes/Staging/components/PromoteSite/utils/helpers';
import {
  EVENT_PROMOTE_DONE,
  PROMOTION_IN_PROGRESS,
} from '~/scenes/Site/scenes/Cloud/scenes/Staging/utils/constants';
import {
  checkIsParentSite,
  checkIsStagingSite,
} from '~/scenes/Site/scenes/Cloud/scenes/Staging/utils/helpers';
import { SiteInfoType } from '~/types/site';

/*
  Given a site, determines whether a promotion is currently in progress for
    either 1) the current site in context, or 2) the relevant parent site.

  Currently, only Atomic hosting supports the concepts of staging and promotion.
*/
const usePromotionInfo = (site?: SiteInfoType, isFetching?: boolean) => {
  const { callbacks } = useTenant();
  const isStagingSite = checkIsStagingSite(site);
  const isParentSite = checkIsParentSite(site);
  const isAtomic = site?.subtype === 'atomic';
  let inProgress: boolean = false;
  let queryKey: any[] | undefined = undefined;

  const { data: siteInfo, isSuccess: isSuccessSiteInfo } = useSitesInfo(
    Number(site?.staging?.parent_site_id),
    {
      query: {
        // If we're in context of a staging site, then we need info about its parent site
        enabled: !!site?.id && isAtomic && isStagingSite,
      },
    },
  );

  if (isAtomic && (isStagingSite || isParentSite)) {
    let hostingId = undefined;
    let hostingBackRef = undefined; // To generate keys for the queries we actually want to invalidate
    let stagingSiteId = undefined;
    let parentSiteId = undefined;
    const hostingIds = [];
    const siteIds = [];

    if (isStagingSite && isSuccessSiteInfo) {
      // If we're in context of a staging site, check the parent site's promotion status
      hostingBackRef = siteInfo?.data?.account_back_ref;
      hostingId = String(siteInfo?.data?.account_id); // HAL Atomic child account id - NOT account back ref
      hostingIds.push(hostingId);

      stagingSiteId = String(site?.id); // this staging site's parent site's id
      parentSiteId = String(siteInfo?.data?.id); // this staging site's id
      siteIds.push(stagingSiteId, parentSiteId);
    } else if (isParentSite) {
      // otherwise check own promotion status
      hostingBackRef = site?.account_back_ref;
      hostingId = String(site?.account_id); // HAL Atomic child account id - NOT account back ref
      hostingIds.push(hostingId);

      stagingSiteId = String(site?.staging?.promoting_from); // the parent's staging site's id
      parentSiteId = String(site?.id); // this parent site's id
      siteIds.push(stagingSiteId, parentSiteId);
    }

    queryKey = generateEventEntryQueryKey(EVENT_PROMOTE_DONE, {
      hosting: [...hostingIds],
      site: [...siteIds],
    } as ResourceIdTypes);

    // Check to see if this site has a pending promotion event invalidation entry
    const eventQueryData: EventEntryQueryData | undefined =
      queryClient.getQueryData(queryKey);

    const pendingInvalidationExists =
      !!queryKey &&
      queryKey.length > 2 && // the default queryKey has at least two elements (base key and event)
      !!eventQueryData?.queries &&
      eventQueryData?.queries?.length > 0;

    if (
      // If we have a pending invalidation entry for this site or its parent
      pendingInvalidationExists ||
      // Make sure siteInfo from siteContext is finished getting fresh data
      (!isFetching &&
        // if this site itself is promoting
        ((isParentSite &&
          site?.staging?.promotion_status === PROMOTION_IN_PROGRESS) ||
          // if this site's parent is promoting
          (isStagingSite &&
            !!siteInfo &&
            siteInfo?.data?.staging?.promotion_status ===
              PROMOTION_IN_PROGRESS)))
    ) {
      inProgress = true;

      // Only resubscribe to event polling if a polling entry doesn't already exist AND we aren't currently doing active site polling on the modal
      if (!pendingInvalidationExists) {
        const newQueryKeys: any[] = getStagingPromotionQueryKeys(
          parentSiteId!,
          stagingSiteId!,
          hostingBackRef!,
        );

        callbacks.subscribeToEvent({
          events: [EVENT_PROMOTE_DONE],
          resourceIds: {
            hosting: [...hostingIds],
            site: [...siteIds],
          },
          queryKeys: newQueryKeys,
        } as eventWatchRequestPropOptions);
      }
    }
  }

  type PromotionInfoResponseTypes = {
    inProgress: boolean;
    queryKey?: any[];
  };

  return {
    inProgress,
    queryKey,
  } as PromotionInfoResponseTypes;
};

export default usePromotionInfo;
